'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/app/_components/core/accordion';
import { ComponentSection } from '@/app/_components/core/component-section';
import { SectionIntro } from '@/app/_components/core/section-intro';
import { getItemTrackId, getSectionProps, getTrackingPrefix } from '@/app/_components/shared/utils/component-helpers';
import { useHash } from '@/app/_hooks/use-hash';
import { AccordionCardListItem } from './components/accordion-card-list-item';
import type {
  AccordionCardListComponentProps,
  AccordionCardListGroup,
} from './accordion-card-list.types';

function AccordionGroupItems({
  groups,
  moduleId,
  trackingPrefix,
}: {
  groups: AccordionCardListGroup[];
  moduleId: string;
  trackingPrefix?: string;
}) {
  return groups.map((group, groupIndex) => (
    <AccordionItem key={group.id} value={group.id}>
      <AccordionTrigger
        className="cursor-pointer text-left text-body-md lg:text-body-md-lg"
        data-track-id={
          trackingPrefix ? `${trackingPrefix}-item-toggle-${getItemTrackId(group.id, groupIndex)}` : undefined
        }
      >
        {group.title}
      </AccordionTrigger>
      <AccordionContent>
        {group.cards.length > 0 ? (
          <div className="grid grid-cols-1 gap-x-6 gap-y-10 pt-4 sm:grid-cols-2 lg:grid-cols-4 xl:gap-x-8">
            {group.cards.map((card, cardIndex) => (
              <AccordionCardListItem
                key={card.id}
                card={card}
                moduleId={moduleId}
                trackingPrefix={
                  trackingPrefix
                    ? `${trackingPrefix}-card-${getItemTrackId(card.id, cardIndex)}`
                    : trackingPrefix
                }
              />
            ))}
          </div>
        ) : null}
      </AccordionContent>
    </AccordionItem>
  ));
}

// Find the group that contains a card with the given anchor ID
function findGroupByAnchor(
  groups: AccordionCardListGroup[],
  hash: string,
  anchorPrefix: string
): string | null {
  if (!hash.startsWith(`${anchorPrefix}-`)) return null;

  const cardId = hash.slice(anchorPrefix.length + 1);
  for (const group of groups) {
    if (group.cards.some((card) => card.id === cardId)) {
      return group.id;
    }
  }
  return null;
}

export default function AccordionCardListComponent({
  component,
  componentPosition,
  isNested,
}: AccordionCardListComponentProps) {
  const { groups, intro, defaultValue, allowMultiple, moduleId } = component;
  const anchorPrefix = moduleId ? `acl-${moduleId}` : 'acl';
  const trackingPrefix = getTrackingPrefix(component);
  const { hash, isReady, version } = useHash();

  // Find which group to open based on hash
  const hashGroupId = useMemo(
    () => (isReady ? findGroupByAnchor(groups, hash, anchorPrefix) : null),
    [isReady, groups, hash, anchorPrefix]
  );

  // Controlled accordion state
  const [openValues, setOpenValues] = useState<string[]>(() => {
    if (defaultValue) return [defaultValue];
    return [];
  });

  // Open the target group when hash points to a card. Reaction to hash
  // navigation (external URL state) plus a scroll side-effect — a legitimate
  // effect, not a derived value.
  useEffect(() => {
    if (!hashGroupId) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: open accordion in response to hash navigation
    setOpenValues((prev) =>
      prev.includes(hashGroupId) ? prev : [...prev, hashGroupId]
    );

    // Scroll to the card after the accordion animation completes
    const timer = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 350);

    return () => { clearTimeout(timer); };
  }, [hashGroupId, hash, version]);

  // Handle user toggling accordion items
  const handleValueChange = useCallback((value: string | string[]) => {
    setOpenValues(Array.isArray(value) ? value : value ? [value] : []);
  }, []);

  if (groups.length === 0) {
    return null;
  }

  return (
    <ComponentSection isNested={isNested}
      {...getSectionProps(component, componentPosition, isNested)}
      className="container py-section-padding-y-mobile xl:py-section-padding-y-desktop"
    >
      {intro && <SectionIntro intro={intro} trackingPrefix={trackingPrefix} />}

      {allowMultiple ? (
        <Accordion
          type="multiple"
          value={openValues}
          onValueChange={handleValueChange}
          className="w-full"
        >
          <AccordionGroupItems groups={groups} moduleId={anchorPrefix} trackingPrefix={trackingPrefix} />
        </Accordion>
      ) : (
        <Accordion
          type="single"
          collapsible
          value={openValues[0] || ''}
          onValueChange={handleValueChange}
          className="w-full"
        >
          <AccordionGroupItems groups={groups} moduleId={anchorPrefix} trackingPrefix={trackingPrefix} />
        </Accordion>
      )}
    </ComponentSection>
  );
}

export { AccordionCardListComponent };
